import { CalendarX, CloudOff, KeyRound, Lock, MailWarning, ShieldOff, Users, type LucideIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { LinkStatusCard } from '@/components/common/LinkStatusCard';
import { ROUTES } from '@/lib/constants/routes';
import { useAuth } from '@/hooks/useAuth';
import { getPrimaryRole } from '@/lib/constants/roles';
import { ROLES } from '@/lib/constants/roles';
import { DATA_ROOM_STRINGS } from '../constants';
import type { RefusalReason } from '../api/data-room.api';

/**
 * Icon per reason. Kept as an exhaustive `Record` of the API union rather than
 * a chain of `reason === ...` checks, so a new reason cannot be added to the
 * contract without one being chosen here (mnr-standards: one lookup per
 * discriminant, never a branch re-tested per derived value). Copy and the
 * destination live in `constants.ts` beside the rest of the feature's strings.
 */
const REFUSAL_ICON: Record<RefusalReason, LucideIcon> = {
  no_active_service: Lock,
  seat_not_assigned: KeyRound,
  company_email_required: MailWarning,
  workspace_suspended: ShieldOff,
  entitlement_expired: CalendarX,
  seat_limit_reached: Users,
  upstream_unavailable: CloudOff,
};

export interface DataRoomRefusalProps {
  /** The backend's reason. Rendered as given - never re-derived, never mapped. */
  reason: RefusalReason;
  /**
   * Runs the launch again, for the one reason that is retryable. Defaults to
   * reloading the interstitial so the component still offers a real retry when
   * rendered without a launch hook to call.
   */
  onRetry?: () => void;
  /**
   * Fills the screen, as a standalone state. Off on the Legal Command Center,
   * where the page's sections follow it (MYN-331).
   */
  fullScreen?: boolean;
}

/**
 * One distinct state per `RefusalReason` (`docs/specs/features/data-room/02-design.md` §12). Each names the
 * reason and offers the way forward: billing for an inactive subscription, the
 * Startup Admin who grants a seat, the company-email requirement, the
 * workspace-level explanation for a suspension, and a retry when the data room
 * itself is unreachable. No reason renders a dead end, and none renders a raw
 * error.
 */
export function DataRoomRefusal({
  reason,
  onRetry = () => window.location.reload(),
  fullScreen = true,
}: DataRoomRefusalProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const base = DATA_ROOM_STRINGS.REFUSAL[reason];
  // M8 gave the billing-shaped refusals somewhere real to point - but only for
  // the person who can act on them. Re-pointing unconditionally would send a
  // member to a page they cannot use, which is the dead end the member copy was
  // written to avoid.
  const isStartupAdmin = getPrimaryRole(user?.roles ?? []) === ROLES.STARTUP_ADMIN;
  const override = isStartupAdmin
    ? DATA_ROOM_STRINGS.REFUSAL_ADMIN_OVERRIDES[
        reason as keyof typeof DATA_ROOM_STRINGS.REFUSAL_ADMIN_OVERRIDES
      ]
    : undefined;
  const { title, body } = base;
  const action = override?.action ?? base.action;
  const actionTo = override?.actionTo ?? base.actionTo;
  const Icon = REFUSAL_ICON[reason];

  return (
    <LinkStatusCard
      fullScreen={fullScreen}
      // Refusal titles run ~40 characters, which wraps to three lines at the
      // default 480px. 640px keeps every reason's heading to two at most.
      maxWidthClassName="max-w-160"
      icon={<Icon size={32} />}
      heading={title}
      body={body}
      primaryAction={{
        label: action,
        onClick: actionTo ? () => navigate(actionTo) : onRetry,
      }}
      ctaLabel={DATA_ROOM_STRINGS.BACK_TO_DASHBOARD}
      ctaTo={ROUTES.DASHBOARD}
      ctaVariant="link"
    />
  );
}
